/**
 * Pure helper: turn a flat list of vendor-tagged picker lines into one
 * requisition draft per distinct vendor. $0 / TBD unit costs are allowed —
 * finance treats $0 as "unpriced". No I/O; node-testable.
 */
import { round2 } from '../../../../shared/money';
import type { RequisitionLine } from '../../../types';

/** A line as it comes out of the order-side vendor-grouped picker. */
export interface VendorPickLine {
  vendorId: string;
  vendorName: string;
  description: string;
  uom: string;
  qty: number;
  unitCost: number;
  sourceQuoteLineId?: string;
}

export interface RequisitionDraft {
  vendorId: string;
  vendorName: string;
  lines: RequisitionLine[];
  totalCost: number;
}

export function groupLinesByVendor(lines: VendorPickLine[]): RequisitionDraft[] {
  const groups = new Map<string, RequisitionDraft>();
  for (const l of lines) {
    if (!l.vendorId || !l.description?.trim()) continue;
    const qty = Math.max(0, l.qty || 0);
    if (qty <= 0) continue;
    // Round unitCost to cents first, then derive lineCost from the rounded
    // unitCost — matches createManualRequisition so that unitCost × qty ===
    // lineCost on the persisted PR line (no "doesn't multiply out" discrepancy).
    const unitCost = round2(Math.max(0, l.unitCost || 0));
    const reqLine: RequisitionLine = {
      description: l.description.trim(),
      uom: (l.uom || 'each').trim() || 'each',
      qty,
      unitCost,
      lineCost: round2(unitCost * qty),
      ...(l.sourceQuoteLineId ? { sourceQuoteLineId: l.sourceQuoteLineId } : {}),
    };
    const draft = groups.get(l.vendorId) ?? {
      vendorId: l.vendorId,
      vendorName: l.vendorName?.trim() || 'Unnamed vendor',
      lines: [],
      totalCost: 0,
    };
    draft.lines.push(reqLine);
    draft.totalCost = round2(draft.totalCost + reqLine.lineCost);
    groups.set(l.vendorId, draft);
  }
  return [...groups.values()];
}
